import { createContext, useContext, useMemo, type ReactNode } from "react";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import type { PlatformLoyer } from "@/store/platform/bloc6";
import { toMonthKey } from "./periods";
import { isImpaye, type Loyer, type LoyerStatut } from "./types";

interface LoyersStats {
  total: number;
  payes: number;
  aVenir: number;
  enRetard: number;
  impayes: number;
  montantDuMois: number;
  montantImpayes: number;
}

interface LoyersContextValue {
  loyers: Loyer[];
  impayes: Loyer[];
  stats: LoyersStats;
  periodes: string[];
  loyersByContrat: (contratId: string) => Loyer[];
  loyersByLocataire: (locataireId: string) => Loyer[];
  loyersByBien: (bienId: string) => Loyer[];
  loyersByImmeuble: (immeubleId: string) => Loyer[];
  getLoyer: (id: string) => Loyer | undefined;
  hasContratsActifs: boolean;
}

const LoyersContext = createContext<LoyersContextValue | null>(null);

function toOwnerLoyer(source: PlatformLoyer): Loyer {
  const statut: LoyerStatut = source.statut === "annule" ? "a-venir" : source.statut;
  return {
    id: source.id,
    contratId: source.contratId,
    immeubleId: source.immeubleId,
    bienId: source.bienId,
    locataireId: source.locataireId,
    periode: source.periode,
    montant: source.montant,
    echeance: source.echeance,
    statut,
    totalPaye: source.totalPaye,
    reste: source.reste,
    joursRetard: source.joursRetard,
  };
}

/**
 * Vue « Propriétaire » des loyers. Supabase est la source de vérité : les
 * échéances sont lues depuis la vue `v_loyers_details` (statut, montant payé,
 * reste à payer, retard calculés en base) et les impayés depuis `v_impayes`.
 * Aucun calcul de loyer n'est dupliqué côté frontend.
 */
export function LoyersProvider({ children }: { children: ReactNode }) {
  const platform = usePlatformData();

  const loyers = useMemo(
    () => platform.loyers.filter((item) => item.scope === "proprietaire").map(toOwnerLoyer),
    [platform.loyers]
  );

  const impayes = useMemo(
    () =>
      platform.impayes
        .filter((item) => item.scope === "proprietaire")
        .map(toOwnerLoyer)
        .filter((loyer) => isImpaye(loyer.statut))
        .sort((a, b) => b.joursRetard - a.joursRetard),
    [platform.impayes]
  );

  const periodes = useMemo(
    () => Array.from(new Set(loyers.map((loyer) => loyer.periode))).sort((a, b) => (a < b ? 1 : -1)),
    [loyers]
  );

  const stats = useMemo<LoyersStats>(() => {
    const moisCourant = toMonthKey(new Date());
    return {
      total: loyers.length,
      payes: loyers.filter((loyer) => loyer.statut === "paye").length,
      aVenir: loyers.filter((loyer) => loyer.statut === "a-venir").length,
      enRetard: loyers.filter((loyer) => loyer.statut === "en-retard").length,
      impayes: loyers.filter((loyer) => loyer.statut === "impaye").length,
      montantDuMois: loyers
        .filter((loyer) => loyer.periode === moisCourant)
        .reduce((sum, loyer) => sum + loyer.montant, 0),
      montantImpayes: loyers.filter((loyer) => isImpaye(loyer.statut)).reduce((sum, loyer) => sum + loyer.reste, 0),
    };
  }, [loyers]);

  const hasContratsActifs = useMemo(
    () => platform.contrats.some((contrat) => contrat.scope === "proprietaire" && contrat.statut === "actif"),
    [platform.contrats]
  );

  const value = useMemo<LoyersContextValue>(
    () => ({
      loyers,
      impayes,
      stats,
      periodes,
      loyersByContrat: (contratId) => loyers.filter((loyer) => loyer.contratId === contratId),
      loyersByLocataire: (locataireId) => loyers.filter((loyer) => loyer.locataireId === locataireId),
      loyersByBien: (bienId) => loyers.filter((loyer) => loyer.bienId === bienId),
      loyersByImmeuble: (immeubleId) => loyers.filter((loyer) => loyer.immeubleId === immeubleId),
      getLoyer: (id) => loyers.find((loyer) => loyer.id === id),
      hasContratsActifs,
    }),
    [loyers, impayes, stats, periodes, hasContratsActifs]
  );

  return <LoyersContext.Provider value={value}>{children}</LoyersContext.Provider>;
}

export function useLoyers(): LoyersContextValue {
  const context = useContext(LoyersContext);
  if (!context) {
    throw new Error("useLoyers doit être utilisé à l'intérieur de LoyersProvider");
  }
  return context;
}